import type { DocumentSection } from '@/shared/api/transcriptor';

import type { TranscriptorConsultation } from '@/entities/transcriptor';

export * from './blankValues';
export * from './blockEditorView';
export * from './blockPresets';
export * from './blockTemplate';
export * from './diagnosisCode';
export * from './documentAnswers';
export * from './encounterDays';
export * from './fieldContour';
export * from './fieldList';
export * from './historyShortcut';
export * from './noteChoiceDom';
export * from './noteChoiceText';
export * from './noteContextKey';
export * from './noteDate';
export * from './noteDeletion';
export * from './noteNumbers';
export * from './noteEditorContent';
export * from './noteFieldSettings';
export * from './noteLinkDom';
export * from './noteOperations';
export * from './noteSurface';
export * from './noteTable';
export * from './noteTeethMarks';
export * from './noteView';
export * from './popoverPlacement';
export * from './presetAnswers';
export * from './presetTemplate';
export * from './schemaDocuments';
export * from './scrollAnchor';
export * from './specialties';
export * from './teethChart';
export * from './templateDocumentDraft';
export * from './templateDraft';
export * from './templateEdit';
export * from './templateFill';
export * from './templateStructure';
export * from './templateValidation';
export * from './templateVersion';
export * from './treatmentLibrary';

export const getSectionText = (section: DocumentSection) =>
  section.user_content?.text ??
  section.content?.map((item) => item.text).join('\n') ??
  '';

export const getSectionCitationStarts = (section: DocumentSection) =>
  section.user_content
    ? []
    : [
        ...new Set(
          (section.content ?? []).flatMap((item) =>
            (item.citations ?? []).flatMap((citation) =>
              typeof citation.timecodeStart === 'number'
                ? [citation.timecodeStart]
                : [],
            ),
          ),
        ),
      ].sort((a, b) => a - b);

export const formatTimecode = (totalSeconds: number) => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}`
    : `${minutes}:${rest}`;
};

// List previews carry no fragments, only the summed audio duration. A
// recording not measured yet is as long as its transcript, which can end
// before the audio does, so the encounter's total wins when it is longer.
export const getRecordedSeconds = (consultation: TranscriptorConsultation) =>
  Math.max(
    consultation.recordings.reduce(
      (total, recording) => total + recording.durationSec,
      0,
    ),
    consultation.audioDuration ?? 0,
  );

// Under a second prints as "0:00": the encounter is still processing or was
// never recorded, so it shows no length rather than an empty recording.
export const formatRecordedLength = (totalSeconds: number) =>
  totalSeconds >= 1 ? formatTimecode(totalSeconds) : undefined;
